import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { map } from 'rxjs/operators';
import { JsonConvert } from 'json2typescript';
import { HrConfigurationService } from 'humanresources-common-shared';
import { PerPeopleQueryModel } from '../../people-picker/model/people-query-model';
import { PerPeopleModel } from '../../../model/per-people-model';

@Injectable({
    providedIn: 'root',
})
export class PerPeopleService {
    endpoint: string;
    readonly serviceRootPattern = '/peopleService/people/';

    constructor(private httpClient: HttpClient, private configService: HrConfigurationService) {
        this.endpoint = configService.config.hr.personnel;
    }

    public delete(id: number): Observable<any> {
        const pattern = '/peopleService/people/' + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public existsByUuid(uuid: string): Observable<Boolean> {
        const url = `${this.endpoint}${this.serviceRootPattern}exists/uuid/${uuid}`;
        return this.httpClient.get<HvlResponse<Boolean>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, Boolean);
            }),
        );
    }

    public queryPage(pagination: PerPeopleQueryModel): Observable<HvlPage<Array<PerPeopleModel>>> {
        const pattern = this.serviceRootPattern + 'page';
        return this.httpClient.post<HvlResponse<HvlPage<PerPeopleModel[]>>>(this.endpoint + pattern, pagination).pipe(
            map((hvlpage) => {
                if (hvlpage.body?.totalElements === 0) {
                    return new HvlPage<Array<PerPeopleModel>>(
                        [],
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }

                const converter = new JsonConvert();
                const model = converter.deserializeArray(hvlpage.body.data, PerPeopleModel);
                return new HvlPage<Array<PerPeopleModel>>(
                    model,
                    hvlpage.body.totalElements,
                    hvlpage.body.totalPages,
                    hvlpage.errorDetail,
                );
            }),
        );
    }

    public getById(id: number): Observable<PerPeopleModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}${id}`;
        return this.httpClient.get<HvlResponse<PerPeopleModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerPeopleModel);
            }),
        );
    }

    public existsById(id: number): Observable<Boolean> {
        const url = `${this.endpoint}${this.serviceRootPattern}exists/${id}`;
        return this.httpClient.get<HvlResponse<Boolean>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, Boolean);
            }),
        );
    }

    public getByUuid(uuid: string): Observable<PerPeopleModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}${uuid}`;
        return this.httpClient.get<HvlResponse<PerPeopleModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerPeopleModel);
            }),
        );
    }

    public update(model: PerPeopleModel): Observable<PerPeopleModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient.put<HvlResponse<PerPeopleModel>>(this.endpoint + pattern, converter.serialize(model, PerPeopleModel )).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerPeopleModel);
            }),
        );
    }

    public exists(queryModel: PerPeopleQueryModel): Observable<Boolean> {
        const pattern = this.serviceRootPattern + 'exists';
        return this.httpClient.post<HvlResponse<Boolean>>(this.endpoint + pattern, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, Boolean);
            }),
        );
    }

    public create(model: PerPeopleModel): Observable<number> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();

        return this.httpClient
            .post<HvlResponse<number>>(this.endpoint + pattern, converter.serialize(model, PerPeopleModel))
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }

    public queryList(queryModel: PerPeopleQueryModel): Observable<Array<PerPeopleModel>> {
        const pattern = this.serviceRootPattern + 'query';
        return this.httpClient.post<HvlResponse<Array<PerPeopleModel>>>(this.endpoint + pattern, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerPeopleModel);
            }),
        );
    }

    public getPeopleList(pagination: PerPeopleQueryModel): Observable<HvlPage<Array<PerPeopleModel>>> {
        const pattern = this.serviceRootPattern + 'getPeopleList';
        return this.httpClient.post<HvlResponse<HvlPage<PerPeopleModel[]>>>(this.endpoint + pattern, pagination).pipe(
            map((hvlpage) => {
                if (hvlpage.body?.totalElements === 0) {
                    return new HvlPage<Array<PerPeopleModel>>(
                        [],
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }

                const converter = new JsonConvert();
                const model = converter.deserializeArray(hvlpage.body.data, PerPeopleModel);
                return new HvlPage<Array<PerPeopleModel>>(
                    model,
                    hvlpage.body.totalElements,
                    hvlpage.body.totalPages,
                    hvlpage.errorDetail,
                );
            }),
        );
    }
}
